/**
 * Firebase web SDK initialization.
 *
 * Loads and configures Firebase for the portal and exposes the app plus an
 * `Auth` instance for the sign-in flow to consume. This mirrors the admin-gui
 * setup: a single `pop.in` Firebase project (no per-partner tenant) with App
 * Check via reCAPTCHA Enterprise so the phone sign-in flow is allowed.
 */

import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth, type Auth } from "firebase/auth";

import { FIREBASE_CONFIG, FIREBASE_ENABLED, RECAPTCHA_ENTERPRISE_KEY } from "@/config";

if (!FIREBASE_ENABLED) {
  // Surface misconfiguration early in the console rather than failing deep in
  // an auth call later. Copy `.env.example` to `.env` to populate these.
  console.warn(
    "[firebase] BUN_PUBLIC_FIREBASE_* env vars are missing — auth will not work until they are set.",
  );
}

// Reuse the existing default app if one is already initialized. `initializeApp`
// throws ("[DEFAULT] already exists") when called twice, which the dev server's
// hot module reloading can trigger when this module re-evaluates.
export const firebaseApp: FirebaseApp = getApps().length
  ? getApp()
  : initializeApp(FIREBASE_CONFIG);

// App Check protects the Firebase backend (notably phone auth) from abuse. It is
// only wired up once a reCAPTCHA Enterprise key is configured; without it the
// SMS sign-in flow will be rejected by the project's App Check enforcement.
// The global flag keeps the dev server's HMR re-evaluation from initializing it
// twice (which throws).
const appCheckScope = globalThis as typeof globalThis & {
  __popinPartnerAppCheckInit?: boolean;
};
if (FIREBASE_ENABLED && RECAPTCHA_ENTERPRISE_KEY && !appCheckScope.__popinPartnerAppCheckInit) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
  appCheckScope.__popinPartnerAppCheckInit = true;
}

/**
 * Lazily construct the `Auth` instance on first use.
 *
 * `getAuth()` eagerly validates the API key and throws when it is absent, so we
 * defer it until something actually needs auth — by which point real
 * `BUN_PUBLIC_FIREBASE_*` credentials should be in the environment.
 */
let authInstance: Auth | undefined;

export function getFirebaseAuth(): Auth {
  if (!authInstance) {
    authInstance = getAuth(firebaseApp);
  }
  return authInstance;
}
